import * as THREE from "three";
import type { Scenery } from "../../prims/scenery";
import type { Kit } from "./kit";

// The castle gate (#156): two round towers with pointed roofs and flags
// either side of an opening `wide` across, joined high overhead by a
// battlemented wall with an arch under it, `h` up to the arch's top. You
// skate through along u. Short curtain walls run out past each tower.

export function castle(kit: Kit, s: Scenery): THREE.Object3D {
  const root = new THREE.Group();
  const wide = s.wide ?? 10;
  const h = s.h ?? 7;
  const stone = s.color ?? "#f2c6d3";
  const trim = "#e8a5b8";
  const roof = "#6a4c93";
  const deep = 2;
  const towerR = 1.7;
  const towerH = h + 3.4;
  const tz = wide / 2 + towerR - 0.2;
  for (const side of [-1, 1]) {
    const z = side * tz;
    kit.cyl(root, towerR, towerR + 0.15, towerH, stone, 0, 0, z, 16, "concrete");
    kit.cyl(root, towerR + 0.3, towerR + 0.3, 0.5, trim, 0, towerH, z, 16, "concrete");
    // Battlements round the top of the tower.
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      kit.box(root, 0.45, 0.5, 0.45, trim, Math.cos(a) * (towerR + 0.1), towerH + 0.5, z + Math.sin(a) * (towerR + 0.1), "concrete");
    }
    const cone = new THREE.Mesh(new THREE.ConeGeometry(towerR + 0.2, 3.2, 16), kit.mat(roof, "paint"));
    cone.position.set(0, towerH + 0.5 + 1.6, z);
    cone.castShadow = true;
    root.add(cone);
    kit.rod(root, new THREE.Vector3(0, towerH + 3.6, z), new THREE.Vector3(0, towerH + 5, z), 0.04, "#5c677d");
    kit.box(root, 0.05, 0.5, 0.9, side < 0 ? "#ffd166" : "#06d6a0", 0, towerH + 4.4, z + side * 0.45, "cloth");
    // Windows.
    for (const y of [h * 0.45, h * 0.8]) kit.box(root, 0.1, 0.9, 0.5, "#3d2c4e", towerR + 0.05, y, z, "glass");
    // Curtain wall running out past the tower.
    kit.box(root, deep * 0.6, h * 0.55, 3, stone, 0, 0, z + side * (towerR + 1.4), "concrete");
    for (let k = 0; k < 3; k++) kit.box(root, deep * 0.6, 0.45, 0.5, trim, 0, h * 0.55, z + side * (towerR + 0.5 + k * 1), "concrete");
  }
  // The wall over the gate, from the arch's top up, with battlements.
  const span = wide + 0.4;
  kit.box(root, deep, 1.8, span, stone, 0, h, 0, "concrete");
  const merlons = Math.round(span / 1.1);
  for (let i = 0; i < merlons; i++) kit.box(root, deep, 0.6, 0.55, trim, 0, h + 1.8, -span / 2 + 0.3 + (i * (span - 0.6)) / (merlons - 1), "concrete");
  // The arch: voussoirs round a half circle under the wall.
  const r = wide / 2;
  const n = 13;
  for (let i = 0; i < n; i++) {
    const a = (i / (n - 1)) * Math.PI;
    const k = r * 0.35;
    const block = kit.box(root, deep * 0.9, 0.5, wide / n + 0.1, trim, 0, h - k + Math.sin(a) * k - 0.25, Math.cos(a) * r, "concrete");
    // Along the arch's curve.
    block.rotation.x = Math.atan2(k * Math.cos(a), r * Math.sin(a));
    // The wall filled in above it.
    const top = h - k + Math.sin(a) * k + 0.2;
    if (h - top > 0.05) kit.box(root, deep * 0.8, h - top, wide / n + 0.1, stone, 0, top, Math.cos(a) * r, "concrete");
  }
  // A banner over the gate.
  kit.box(root, 0.08, 1.1, Math.min(wide * 0.6, 5), "#e63946", deep / 2 + 0.05, h + 0.35, 0, "cloth");
  kit.box(root, 0.1, 0.6, Math.min(wide * 0.6, 5) - 0.6, "#ffd166", deep / 2 + 0.07, h + 0.6, 0, "cloth");
  return root;
}
